import { useEffect } from 'react';
import { useAppSettingsStore, type ThemeMode } from '../stores/appSettingsStore';
import { logWithTimestamp } from '../utils/dateHelpers';

/**
 * Hook pentru gestionarea Dark Mode în Buzunarul
 * 🌙 Integrat cu Carbon Copper Design System
 *
 * Features:
 * - Sincronizare cu settingsStore (Zustand)
 * - Persistență în localStorage
 * - Detectare preferință sistem
 * - Smooth transitions cu TailwindCSS
 * - Suport pentru darkModeUtils din CVA v2
 */
export const useDarkMode = () => {
  const { appSettings, updateAppSettings, getCurrentTheme, setThemeMode } = useAppSettingsStore();
  const isDark = getCurrentTheme() === 'dark';

  /**
   * Inițializare dark mode la primul mount
   * Prioritate: localStorage > settings store > system preference
   */
  useEffect(() => {
    const initializeDarkMode = () => {
      try {
        // 1. Verifică localStorage pentru preferința salvată
        const storedDarkMode = localStorage.getItem('darkMode');
        let shouldBeDark = isDark;

        // TEMPORARILY DISABLED - Dark mode is not ready yet
        shouldBeDark = false; // Always use light mode
        
        // Clear any existing dark mode preference
        localStorage.removeItem('darkMode');

        // Sincronizează toate sursele de adevăr
        if (shouldBeDark !== isDark) {
          setThemeMode(shouldBeDark ? 'dark' : 'light');
        }

        // Aplică tema în DOM
        if (shouldBeDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        // Salvează în localStorage pentru consistență
        localStorage.setItem('darkMode', shouldBeDark.toString());

        logWithTimestamp('🌙 Dark Mode initialized: ' + shouldBeDark);
      } catch (error) {
        console.warn('Failed to initialize dark mode:', error);
      }
    };

    initializeDarkMode();
  }, []); // Run only once on mount

  /**
   * Sincronizează schimbările din store cu DOM și localStorage
   */
  useEffect(() => {
    try {
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      localStorage.setItem('darkMode', isDark.toString());
    } catch (error) {
      console.warn('Failed to sync dark mode:', error);
    }
  }, [isDark]);

  /**
   * Toggle dark mode cu smooth transition
   */
  const toggleDarkMode = () => {
    // TEMPORARILY DISABLED - Dark mode is not ready yet
    console.log('Dark mode is temporarily disabled');
    return false;
  };

  /**
   * Set dark mode explicit (pentru componente care au toggle switches)
   */
  const setDarkMode = (value: boolean) => {
    try {
      if (value !== isDark) {
        setThemeMode(value ? 'dark' : 'light');
        logWithTimestamp('🌙 Dark Mode set to: ' + value);
      }
    } catch (error) {
      console.error('Failed to set dark mode:', error);
    }
  };

  /**
   * Listener pentru schimbări de system preference
   */
  useEffect(() => {
    // Nu atașăm listener pentru preferința sistemului în faza beta
    return () => undefined;
  }, [setDarkMode]);

  /**
   * Utility functions expuse pentru componente
   */
  const utils = {
    /**
     * Verifică dacă dark mode este activ
     */
    isDarkMode: () => isDark,

    /**
     * Obține clasa CSS pentru tema curentă
     */
    getThemeClass: () => isDark ? 'dark' : 'light',

    /**
     * Obține culorile pentru tema curentă din Carbon Copper palette
     */
    getThemeColors: () => {
      return isDark ? {
        primary: '#F97316',        // Copper 400 - lighter pentru dark backgrounds
        secondary: '#78716C',      // Carbon 400 - lighter pentru contrast
        accent: '#FB923C',         // Copper 300 - accent pentru dark mode
        success: '#10B981',        // Emerald 500 - visibility optimizată
        warning: '#F59E0B',        // Amber 500 - contrast pentru dark
        error: '#EF4444',          // Ruby 500 pentru dark readability
        neutral: '#D6D3D1',        // Carbon 300 pentru dark text
        background: '#0A0908',     // Carbon 950 - sophisticated dark background
        surface: '#1C1917',        // Carbon 800 - card/surface background
        border: '#292524',         // Carbon 700 - border pentru dark mode
      } : {
        primary: '#EA580C',        // Copper 500 - main copper
        secondary: '#57534E',      // Carbon 500 - medium carbon
        accent: '#EA580C',         // Copper accent
        success: '#059669',        // Emerald 600 - main success
        warning: '#D97706',        // Amber 600 - main warning
        error: '#DC2626',          // Ruby 600 - main error
        neutral: '#78716C',        // Carbon 400 - neutral text
        background: '#FAFAF9',     // Carbon 50 - warm white background
        surface: '#FFFFFF',        // Pure white pentru cards
        border: '#E7E5E4',         // Carbon 200 - light borders
      };
    },

    /**
     * Reset la system preference (șterge localStorage)
     */
    resetToSystemPreference: () => {
      try {
        localStorage.removeItem('darkMode');
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setDarkMode(systemPrefersDark);
        logWithTimestamp('🌙 Reset to system preference: ' + systemPrefersDark);
      } catch (error) {
        console.error('Failed to reset to system preference:', error);
      }
    },
  };

  return {
    isDark,
    toggleDarkMode,
    setDarkMode,
    ...utils,
  };
};

export default useDarkMode;
